/**
 * ADR-0092 / ADR-0102 / ADR-0192 — Console-side types for the file-step
 * upload sniff + commit + quickcreate.
 *
 * Mirrors the hub shapes in `atlas-hub/services/hub/src/pdo/types.ts` +
 * `pdo/uploadSniff.ts` + `pdo/fileProviderUpload.ts`. Kept here (rather than
 * imported from a shared package) because Console doesn't import from
 * atlas-hub directly — these shapes travel by HTTP JSON.
 * `pdoUploadTypes.test.ts` holds this file to a fixture copied from the hub.
 */

export type PdoSourceType = "upstream" | "upload";

export type UploadParser = "xlsx" | "csv" | "tsv" | "json" | "jsonl" | "parquet";

export type UploadRetentionPolicy = "replace" | "snapshot";

export type XlsxParserConfig = {
  sheet_name: string;
  header_row: number;
  skip_blank_leading_rows: boolean;
};

export type CsvParserConfig = {
  delimiter: string;
  quote: string;
  has_header: boolean;
  header_row_offset: number;
};

export type JsonParserConfig = {
  array_path: string;
  flatten_depth: 0 | 1;
};

export type JsonlParserConfig = {
  flatten_depth: 0 | 1;
};

export type ParquetParserConfig = Record<string, never>;

export type UploadParserConfig =
  | XlsxParserConfig
  | CsvParserConfig
  | JsonParserConfig
  | JsonlParserConfig
  | ParquetParserConfig;

export type PersistNodeUploadConfig = {
  parser: UploadParser;
  parser_config: UploadParserConfig;
  retention_policy: UploadRetentionPolicy;
  snapshot_max_versions?: number;
};

// ---------------------------------------------------------------------------
// Sniff response shapes (must match hub: src/pdo/uploadSniff.ts)
// ---------------------------------------------------------------------------

/** The platform domain — the ADR-0147 vocabulary the step declares in (`lib/columnDomain.ts` ColumnDomain). */
export type AtlasDomain = "string" | "number" | "date" | "boolean";

export type ResolutionMode =
  | "exact_match"
  | "sticky_mapping"
  | "suggested"
  | "unresolved"
  | "new_column"
  | "missing_column";

export type SourceColumnResolution = {
  source_header: string;
  normalized_header: string;
  /** Sniff vocabulary (`text | integer | numeric | timestamp | date | boolean | json`) — informational; never shown raw. */
  inferred_type: string;
  resolution: {
    mode: ResolutionMode;
    canonical_name: string | null;
    confidence: number | null;
    alternatives?: Array<{ canonical_name: string; confidence: number }>;
  };
};

/** ADR-0192 §6.1 — a sniffed column with what the step already declares for it. */
export type SniffColumn = SourceColumnResolution & {
  /** The step's declared domain for the column this header resolves to; null on a first upload / unresolved. */
  declared_type: AtlasDomain | null;
  /** False when every cell in the file's column is blank — such a column agrees with any declaration. */
  has_values: boolean;
};

export type StructuralDriftPreview = {
  new_columns: Array<{ canonical_name: string; type: string }>;
  removed_columns: Array<{ canonical_name: string; type: string }>;
  /** Declared → file, domain vocabulary. Always `[]` on a locked step. */
  type_changes: Array<{ canonical_name: string; from: string; to: string }>;
};

export type SniffResult = {
  /** Server-side temp-file handle. Valid for 1 hour or until upload-success. */
  sniff_id: string;
  /** The filename the user selected — pass back on upload. */
  original_filename: string;
  parser: UploadParser;
  detected: {
    sheets?: string[];
    delimiter?: string;
    header_row: number;
    array_path?: string;
  };
  applied_config: UploadParserConfig;
  /** True only when the step has no current upload. */
  is_first_upload: boolean;
  columns: SniffColumn[];
  missing_canonical_columns: Array<{ canonical_name: string; type: string }>;
  /** The step's declared schema: names from the current upload, types from the step. `[]` on a first upload. */
  declared_columns: Array<{ canonical_name: string; type: AtlasDomain }>;
  /** The step's schema-lock state; false for the tenant-scoped quickcreate sniff. */
  schema_locked: boolean;
  /** The step's sticky header→column renames; `{}` without a step. */
  sticky_column_renames: Record<string, string>;
  preview_rows: unknown[][];
  row_count: number;
  duplicate_source_headers: Array<{ normalized: string; positions: number[] }>;
  structural_drift_preview: StructuralDriftPreview | null;
};

// ---------------------------------------------------------------------------
// Upload request + refusal shapes (must match hub: src/pdo/fileProviderUpload.ts)
// ---------------------------------------------------------------------------

export type ColumnResolutionInput = {
  source_header: string;
  resolution: "existing" | "new" | "skip";
  canonical_name: string | null;
  /** FIRST UPLOAD ONLY — an AtlasDomain seed; null on every later upload. */
  type_override: string | null;
};

/** ADR-0192 §4.3 step 5 — a keyed, loaded stored dataset downstream of the step (carried on a drift refusal). */
export type DownstreamKeyedPersist = { node_id: string; label: string; pdo_name: string };

/** The `extra` of a `409 schema_drift_requires_confirmation` refusal. */
export type SchemaDriftRefusal = {
  drift: StructuralDriftPreview;
  downstream_keyed_persists: DownstreamKeyedPersist[];
};

// ---------------------------------------------------------------------------
// The landing zone's bounds, and the words the wizard uses while a file moves.
//
// Served by the hub on `/api/tenant/me` (`upload_limits`), never a console
// literal. There is deliberately NO per-file cap: the only bound is the
// workspace's budget for uploads in progress, so the wizard never states a
// limit on the file a person picked. The pre-check still runs before a byte
// moves, because the server can only refuse after the whole file has crossed
// the wire and it says less when it does.
// ---------------------------------------------------------------------------

/** The landing zone's bounds, as `/api/tenant/me` serves them. */
export type UploadLimits = {
  /** Bytes of uploads-in-progress this workspace may hold at once. */
  tenant_budget_bytes: number;
  /** The largest file this release reads in one request. Absent on an older hub. */
  inline_read_max_bytes?: number;
};

/** Bytes → megabytes for people: whole MB as an integer, otherwise one decimal. */
export function formatMb(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return Number.isInteger(mb) ? String(mb) : mb.toFixed(1);
}

/**
 * True when this upload cannot possibly land, so the wizard should say so
 * rather than spend a person's bandwidth finding out.
 *
 * Checked against the DECODED size — the file as it sits on disk — because that
 * is what the hub's counter measures and what the budget is denominated in.
 * Compression changes what crosses the wire, not what is stored.
 *
 * This is not a per-file cap dressed up as one. A file over the workspace's
 * whole budget is a file that cannot fit however the workspace is tidied, which
 * is worth saying up front; everything smaller is the hub's to adjudicate
 * against what the workspace is holding right now, which the console cannot
 * know and must not guess.
 */
export function exceedsUploadLimit(file: { size: number }, limits: UploadLimits): boolean {
  const bounds = [limits.tenant_budget_bytes, limits.inline_read_max_bytes].filter(
    (n): n is number => typeof n === "number" && n > 0,
  );
  return bounds.length > 0 && file.size > Math.min(...bounds);
}

/**
 * The refusal shown instead of an upload.
 *
 * Names the file, its size, the bound it met, and — the part the 2026-09-12
 * defect skipped entirely — that nothing was sent. It reads as a statement
 * about this workspace or this release, never as "your file is too big",
 * because "convert your file" was ruled out as a product answer.
 */
export function fileTooLargeMessage(file: { name: string; size: number }, limits: UploadLimits): string {
  const inline = limits.inline_read_max_bytes;
  if (typeof inline === "number" && inline > 0 && file.size > inline) {
    return `${file.name} is ${formatMb(file.size)} MB. Atlas reads files up to ${formatMb(inline)} MB in one go today; larger files are the next step on this lane. Nothing was sent.`;
  }
  return `${file.name} is ${formatMb(file.size)} MB, which is more than this workspace's ${formatMb(limits.tenant_budget_bytes)} MB of room for uploads in progress. Nothing was sent.`;
}

// ---------------------------------------------------------------------------
// Wizard labels for the three things that happen after a person picks a file.
//
// Pure functions in `lib/` on purpose: the console has no component tests, so
// logic that must be right lives where a test can reach it. Every one of these
// is a sentence a reader sees while nothing else on screen is moving.
// ---------------------------------------------------------------------------

/** Step 1 — the file is being compressed in the browser before it is sent. */
export function compressingLabel(file: { name: string; size: number }): string {
  return `Compressing ${file.name} (${formatMb(file.size)} MB)…`;
}

/**
 * Step 2 — bytes are on the wire. Both numbers are the COMPRESSED size, because
 * that is what is actually moving; showing progress against the original size
 * would stall visibly at a number that is never reached.
 *
 * `total` is 0 until the browser knows the length, and a percentage of an
 * unknown total is a lie, so until then it reports only what has gone.
 */
export function uploadProgressLabel(loadedBytes: number, totalBytes: number): string {
  if (!totalBytes || totalBytes <= 0) return `${formatMb(loadedBytes)} MB sent…`;
  const loaded = Math.min(loadedBytes, totalBytes);
  return `${formatMb(loaded)} of ${formatMb(totalBytes)} MB`;
}

/**
 * Step 3 — the bytes have landed and the hub is reading the file. No progress
 * is available here (the server is parsing, not receiving), and inventing one
 * would be worse than saying plainly what is happening.
 */
export const ANALYZING_LABEL = "Analyzing…";
